import { useState, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import "./layoutStyles.css";
import './style.css'
import CookieAcceptance from "../utils/cookieAcceptance";
import { GetHtmlFiles } from "../api/getHtmlFile";
import parse from 'html-react-parser';
import Logo from "../assets/Logo1.png"
import footerLogo from '../assets/footerLogo.jpg';

// Extend the Window interface to include jQuery
declare global {
    interface Window {
        jQuery?: any;
        $?: any;
    }
}

const AuthLayout = () => {
    const location = useLocation();
    const isContactUsPage = location.pathname === '/contactus';
    const isRootPath = location.pathname === '/';
    const [htmlContent, setHtmlContent] = useState<string | null>(null);
    const scriptsExecuted = useRef<boolean>(false);
    const addedScripts = useRef<HTMLScriptElement[]>([]);

    useEffect(() => {
        const getHtmlData = async (): Promise<void> => {
            const data = await GetHtmlFiles();
            const cleanedUpData = data.replace(/<!DOCTYPE[^>]*>/g, '').replace(/<\/?html[^>]*>/g, '').replace(/<\/?body[^>]*>/g, '').replace(/<\/?head[^>]*>/g, '');
            setHtmlContent(cleanedUpData);
        };

        getHtmlData();
    }, [])

    useEffect(() => {
        if (htmlContent && !scriptsExecuted.current) {
            const ensureJQuery = new Promise<void>((resolve, reject) => {
                // Check if jQuery is already loaded
                if (window.jQuery) {
                    resolve();
                    return;
                }
                
                const script = document.createElement("script");
                script.src = "https://code.jquery.com/jquery-1.11.3.min.js";
                script.onload = () => resolve();
                script.onerror = () => reject(new Error("Failed to load jQuery"));
                document.head.appendChild(script);
            });

            ensureJQuery
                .then(() => {
                    const container = document.createElement("div");
                    container.innerHTML = htmlContent;
                    const scriptTags = container.querySelectorAll("script");
                    
                    scriptTags.forEach((script) => {
                        try {
                            const newScript = document.createElement("script");
                            
                            if (script.src) {
                                // For external scripts, check if already loaded
                                const existingScript = document.querySelector(`script[src="${script.src}"]`);
                                if (!existingScript) {
                                    newScript.src = script.src;
                                    document.body.appendChild(newScript);
                                    addedScripts.current.push(newScript);
                                }
                            } else if (script.textContent) {
                                // For inline scripts, wrap in try-catch to handle redeclaration errors
                                const wrappedCode = `
                                    try {
                                        ${script.textContent}
                                    } catch (e) {
                                        if (e instanceof SyntaxError && e.message.includes('already been declared')) {
                                            console.warn('Script already executed, skipping:', e.message);
                                        } else {
                                            throw e;
                                        }
                                    }
                                `;
                                newScript.textContent = wrappedCode;
                                document.body.appendChild(newScript);
                                addedScripts.current.push(newScript);
                            }
                        } catch (error: any) {
                            console.error("Error executing script:", error);
                        }
                    });
                    
                    scriptsExecuted.current = true;
                })
                .catch((error: Error) => console.error("Error ensuring jQuery:", error));
        }
        
        // Cleanup function
        return () => {
            if (isRootPath) {
                // Clean up added scripts when component unmounts
                addedScripts.current.forEach(script => {
                    if (script.parentNode) {
                        script.parentNode.removeChild(script);
                    }
                });
                addedScripts.current = [];
                scriptsExecuted.current = false;
            }
        };
    }, [htmlContent, isRootPath])

    const renderHeader = () => (
        <header className="pr-10 pl-10 headerImageContainer">
            <div className="flex justify-between items-center">
                <img src={Logo} alt="Vww_Logo" className="w-100 h-12 cursor-pointer" />
                <div className="flex-grow"></div>
                {isContactUsPage ? (
                    <a href="/" className="customeLinkBtn text-sm md:text-base">
                        Login
                    </a>
                ) : (
                    <div className="contactContainer flex items-center">
                        <a href="/contactus" className="customLinkBtn text-sm md:text-base">
                            Contact US
                        </a>
                    </div>
                )}
            </div>
        </header>
    );

    if (isRootPath) {
        return (
            <div className="main flex flex-col justify-between min-h-screen">
                {renderHeader()}
                <main>
                    <div className="mb-auto flex flex-row">
                        <div className="w-[50%]">
                            <Outlet />
                        </div>
                        {htmlContent && (
                            <div className="w-[50%] py-15 px-2 pt-8 h-fit">
                                <div className="p-2">{parse(htmlContent)}</div>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center justify-center">
                        <div className="w-[30%] ">
                            <img src={footerLogo} alt="Vww_Logo_Footer" />
                            <p className="text-center">Scan Solutions &copy; 2025</p>
                        </div>
                    </div>
                </main>
                <footer className="w-full h-auto bg-gray-200 py-2 footer">
                    <CookieAcceptance />
                </footer>
            </div>
        )
    }

    return (
        <div className="main flex flex-col justify-between min-h-screen">
            {renderHeader()}
            <main className="mb-auto">
                <div className="mx-auto h-full">
                    <Outlet />
                </div>
            </main>
            <footer className="w-full h-auto bg-gray-200 py-2 footer">
                <CookieAcceptance />
            </footer>
        </div>
    );
}

export default AuthLayout